import { useEffect, useState, useCallback } from "react";
import { Mail, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
// iter91 — the two named helpers below were being CALLED at lines 15 & 37
// but never imported. This caused a silent ReferenceError swallowed by
// the try/catch into a generic "Failed to load subscribers" toast — even
// though the /admin/newsletter-subscribers endpoint was returning data
// correctly. Adding the imports fixes both the list AND the CSV download.
import { adminListNewsletterSubscribers, adminNewsletterCsvUrl } from "../../lib/api";

export function NewsletterSubscribersTab({ pw }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminListNewsletterSubscribers(pw);
      setSubs(res.subscribers || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = filter
    ? subs.filter((s) => `${s.email} ${s.name || ""}`.toLowerCase().includes(filter.toLowerCase()))
    : subs;

  const sourceCounts = subs.reduce((acc, s) => {
    acc[s.source || "popup"] = (acc[s.source || "popup"] || 0) + 1;
    return acc;
  }, {});

  const downloadCsv = () => {
    // Built-in fetch with admin header — apiClient header isn't on download link
    const url = adminNewsletterCsvUrl();
    fetch(url, { headers: { "X-Admin-Password": pw } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "newsletter_subscribers.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("CSV download failed"));
  };

  return (
    <div data-testid="newsletter-subscribers-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl uppercase">Newsletter Subscribers</h2>
          <p className="font-mono text-xs text-zinc-500 mt-1">
            Captured via popup, footer, or manual signup. Welcome email auto-fires WELCOME10.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text" placeholder="Filter email / name" value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="subscriber-filter"
            className="bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 font-body text-sm outline-none w-64"
          />
          <button onClick={refresh} className="btn-tc-secondary inline-flex items-center gap-2 text-xs h-10" data-testid="subscriber-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={downloadCsv} className="btn-tc-primary inline-flex items-center gap-2 text-xs h-10" data-testid="subscriber-csv">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="surface-card p-4">
          <div className="font-display text-3xl">{subs.length}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">Total</div>
        </div>
        <div className="surface-card p-4">
          <div className="font-display text-3xl">{subs.filter((s) => s.welcome_sent).length}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">Welcomed</div>
        </div>
        <div className="surface-card p-4">
          <div className="font-display text-3xl">{sourceCounts.popup || 0}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">From Popup</div>
        </div>
        <div className="surface-card p-4">
          <div className="font-display text-3xl">{subs.filter((s) => s.unsubscribed).length}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">Unsubscribed</div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="surface-card p-12 text-center">
          <Mail size={32} className="mx-auto mb-3 text-zinc-700" />
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">
            {filter ? "No subscribers match that filter" : "No subscribers yet"}
          </div>
        </div>
      ) : (
        <div className="surface-card overflow-x-auto" data-testid="subscribers-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#222]">
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">EMAIL</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">NAME</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">SOURCE</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">SIGNED UP</th>
                <th className="font-mono text-[10px] tracking-widest text-zinc-500 p-4">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.email} className="border-b border-[#222] last:border-b-0">
                  <td className="p-4 font-mono text-sm">{s.email}</td>
                  <td className="p-4 font-ui text-sm">{s.name || "—"}</td>
                  <td className="p-4 font-mono text-xs text-zinc-400 uppercase">{s.source || "popup"}</td>
                  <td className="p-4 font-mono text-xs text-zinc-400">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-4">
                    {s.unsubscribed ? (
                      <span className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase">Unsubscribed</span>
                    ) : s.welcome_sent ? (
                      <span className="font-mono text-[10px] tracking-widest text-emerald-400 uppercase">✓ Welcomed</span>
                    ) : (
                      <span className="font-mono text-[10px] tracking-widest text-amber-400 uppercase">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NewsletterSubscribersTab;
